// As we're using immer
/* eslint-disable no-param-reassign */
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { isArray, uniqBy } from 'lodash';
import { DocumentSkelton } from 'api/document/types/document';
import { Task } from 'api/tasks';
import { RootState } from 'data';
import {
  ResourceSection,
  ActiveDocumentInfo,
  DocumentState,
  EditorModes,
  ContentTaskIdMap,
  TaskSectionNameIdMap,
  SectionNameIdMap,
} from './constants';
import { TaskSectionMeta } from './saga/tasks/id-name-map';

const INITIAL_STATE: DocumentState = {
  tasks: {
    items: [],
    apiDone: false,
    loading: false,
    documentRef: {},
    activeTaskId: null,
    sectionNameIdMap: {},
  },
  resourceSections: {
    items: [],
  },
  items: [],
  active: {
    documentId: null,
    projectId: null,
    publicSection: {
      type: EditorModes.Public,
    },
    privateSection: {
      type: EditorModes.Private,
    },
  },
};
const DocumentsSlice = createSlice({
  name: 'documents',
  initialState: INITIAL_STATE,
  reducers: {
    setDocuments(state, action: PayloadAction<DocumentSkelton[]>) {
      state.items = action.payload;
    },
    setActiveDocument(
      state,
      action: PayloadAction<{
        documentId?: number;
        projectId?: number;
        publicSection?: {
          id?: number;
          type: EditorModes.Public;
        };
        privateSection?: {
          id?: number;
          type: EditorModes.Private;
        };
      }>,
    ) {
      state.active = {
        ...state.active,
        ...action.payload,
      };
    },
    setTasksLoading(state, action: PayloadAction<boolean>) {
      state.tasks.loading = action.payload;
    },
    updateActiveTask(state, action: PayloadAction<number | null>) {
      state.tasks.activeTaskId = action.payload;
    },
    setTasksDocMap(state, action: PayloadAction<ContentTaskIdMap>) {
      state.tasks.documentRef = {
        ...state.tasks.documentRef,
        ...action.payload,
      };
    },
    appendToTasksDocMap(state, action: PayloadAction<ContentTaskIdMap>) {
      state.tasks.documentRef = {
        ...state.tasks.documentRef,
        ...action.payload,
      };
    },
    setTaskSectionNameIdMap(
      state,
      action: PayloadAction<TaskSectionNameIdMap>,
    ) {
      state.tasks.sectionNameIdMap = {
        ...state.tasks.sectionNameIdMap,
        ...action.payload,
      };
    },
    updateTasksDocMapWithKeys(state, action: PayloadAction<string[]>) {
      const { parentDocId } = state.tasks.documentRef[action.payload[0]] || {};

      Object.keys(state.tasks.documentRef).forEach((key) => {
        if (
          state.tasks.documentRef[key] &&
          state.tasks.documentRef[key]?.parentDocId === parentDocId
        ) {
          state.tasks.documentRef[key].isActive = false;
        }
      });
      action.payload.forEach((key) => {
        if (state.tasks.documentRef[key]) {
          state.tasks.documentRef[key].isActive = true;
        }
      });
    },
    setDocumentResources(
      state,
      action: PayloadAction<
        (ResourceSection<Partial<Task>> | TaskSectionMeta)[]
      >,
    ) {
      if (
        state.resourceSections.items &&
        isArray(state.resourceSections.items)
      ) {
        state.resourceSections.items = state.resourceSections.items.concat(
          action.payload,
        );
      } else {
        state.resourceSections.items = action.payload;
      }
    },
    updateResourceSection(
      state,
      action: PayloadAction<ResourceSection<Partial<Task>>>,
    ) {
      state.resourceSections.items = uniqBy(
        [action.payload, ...state.resourceSections.items],
        'resourceSectionId',
      );
    },
  },
});

export const selectDocuments = (state: RootState): DocumentSkelton[] =>
  state.documents.items;
export const selectActiveDocumentInfo = (
  state: RootState,
): ActiveDocumentInfo => state.documents.active;
export const selectDocumentActiveTaskId = (state: RootState): number | null =>
  state.documents.tasks.activeTaskId;
export const selectDocumentTasksContentMap = (
  state: RootState,
): ContentTaskIdMap => state.documents.tasks.documentRef;
export const selectTaskSectionIdNameMap = (
  state: RootState,
): { [key: number]: SectionNameIdMap } =>
  state.documents.tasks.sectionNameIdMap;

export const selectDocumentResourceSections = createSelector(
  (state: RootState): (ResourceSection<Partial<Task>> | TaskSectionMeta)[] =>
    state.documents.resourceSections.items,
  (resourceSections) => resourceSections,
);

// TODO: Check whether I need this. let saga take care of this?
export const {
  setDocuments,
  setActiveDocument,
  setTasksLoading,
  updateActiveTask,
  setTasksDocMap,
  appendToTasksDocMap,
  setTaskSectionNameIdMap,
  updateTasksDocMapWithKeys,
  setDocumentResources,
  updateResourceSection,
} = DocumentsSlice.actions;
export const documentsReducer = DocumentsSlice.reducer;
